import React, { useEffect, useState, useRef } from "react";
// NAVIGATION
import { useLocation } from "react-router-dom";
// ANT DESIGN COMPONENTS
import { Table, Tag, Button, InputNumber, Tooltip } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faCircleCheck, faXmarkCircle } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { updateBank } from "../../../redux/banks/bankSlice";
import { updateOpenDrawerAssignInterfaceBank } from "../../../redux/keys/keysSlice";
import {
  useLazyGetBankQuery,
  useDeleteBankKeyMutation,
  usePostBankKeyMutation,
  usePutBankKeyMutation,
  keyAPI,
} from "../../../redux/keys/keysAPI";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// UTILS
import { getTableRowClass } from "../../../utils/utils";
// INTERFACES
import { RootState } from "../../../app/store";
import { BankInterface } from "../../../interfaces/Bank.interface";
// COMPONENTS
import TableNoDataBox from "../../components/table/TableNoDataBox";
import GlobalAlert2 from "../../home/GlobalAlert2";
import SelectMessageType from "../../components/select/SelectMessageType";
import ButtonSquareSave from "../../components/buttons/ButtonSquareSave";
import ButtonSquareRefresh from "../../components/buttons/ButtonSquareRefresh";
import ButtonSquareUploadCSV from "../../components/buttons/ButtonSquareUploadCSV";
import ButtonSquareDownloadCSV from "../../components/buttons/ButtonSquareDownloadCSVKeys";
import ButtonSquareCode from "../../components/buttons/ButtonSquareCode";
// CONSTANTS
import { BLANK_BANK } from "../../../constants/bank";
// UTILS
import { updateSpecificKeys, buildCustomKeysObject, buildAssignedKeysobject } from "./utils";
// CUSTOM HOOKS
import useGetAllMessages from "../../../customHooks/GET_requests/useGetAllMessages";

function KeysBankTable() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const isFirstRender = useRef(true);
  const { Column } = Table;
  const [t] = useTranslation("global");
  const location = useLocation();
  const bankId = new URLSearchParams(location.search).get("idBank");
  const dispatch = useDispatch();

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [totalRows, setTotalRows] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<any>({});
  const [data, setData] = useState<any>([]);
  const [interfaceKeys, setInterfaceKeys] = useState<{ name: string; type: string }[]>([]);
  const [bank, setBank] = useState<BankInterface>({ ...BLANK_BANK });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [messageTypeSelected, setMessageTypeSelected] = useState<string>("ALL");

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [
    triggerGetBank,
    {
      data: dataGetBank,
      isLoading: isLoadingGetBank,
      isFetching: isFetchingGetBank,
      isError: isErrorGetBank,
      error: errorGetBank,
    },
  ] = useLazyGetBankQuery();

  const [
    triggerDeleteKey,
    {
      isLoading: isLoadingDeleteKey,
      isSuccess: isSuccessDeleteKey,
      error: errorDeleteKey,
      isError: isErrorDeleteKey,
    },
  ] = useDeleteBankKeyMutation();

  const [
    triggerPostKey,
    {
      isSuccess: isSuccessPostKey,
      isLoading: isLoadingPostKey,
      isError: isErrorPostKey,
      error: errorPostKey,
      reset: resetPostKey,
    },
  ] = usePostBankKeyMutation();

  const [
    triggerPutKey,
    {
      isSuccess: isSuccessPutKey,
      isError: isErrorPutKey,
      error: errorPutKey,
      isLoading: isLoadingPutKey,
      reset: resetPutKey,
    },
  ] = usePutBankKeyMutation();

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const { messages } = useGetAllMessages();

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const launchIsLoading = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 2000);
  };

  const buildFirstData = (bankInterfaceKeys: any, msgs: any) => {
    const newData = buildCustomKeysObject(msgs, bankInterfaceKeys, "");
    if (newData && newData.length > 0 && bank && bank.customKeys) {
      const copy = buildAssignedKeysobject(newData, bank.customKeys);
      setData(copy);
    } else {
      setData(newData);
    }
  };

  const getInitialData = async () => {
    if (bankId && bankId !== "") {
      const token = await GETJwtToken();
      dispatch(keyAPI.util.resetApiState());
      await triggerGetBank({ token, bankId }).unwrap();
    }
  };

  const onClickDelete = async (item: any) => {
    const token = await GETJwtToken();
    triggerDeleteKey({ id: item.idKey, token });
  };

  const onChangeInputNumber = (newValue: number, item: string, index: number) => {
    const copy = structuredClone(data);
    copy[index][item] = newValue;
    copy[index].isValid = true;
    setData(copy);
  };

  const onClickSave = async () => {
    const token = await GETJwtToken();
    const requests = data.map((item: any) => {
      if (item.isValid) {
        const newValue = interfaceKeys.reduce((acc: any, key: any) => {
          acc[key] = item[key];
          return acc;
        }, {});
        const requestBody = {
          bank_id: bank.id,
          encoded_type: item.encoded_type,
          message_content: item.idType,
          message_value: item.message_value,
          fbs_device_type: item.fbs_device_type,
          value: JSON.stringify(newValue),
        };
        return item.methodType === "POST"
          ? triggerPostKey({ token, body: requestBody })
          : triggerPutKey({ token, id: item.idKey, body: requestBody });
      }
      return true;
    });
    await Promise.all(requests);
  };

  const setUploadCSVData = (jsonData: any) => {
    const updatedObj1 = updateSpecificKeys(data, jsonData, interfaceKeys);
    setData(updatedObj1);
  };

  const onChangeMessageType = (newValue: string) => {
    setMessageTypeSelected(newValue);
  };

  const handleOpenAssignInterface = () => {
    dispatch(updateBank(bank));
    dispatch(updateOpenDrawerAssignInterfaceBank(true));
  };

  const refreshPage = () => {
    if (isFirstRender.current) return;
    launchIsLoading();
    getInitialData();
    resetPostKey();
    resetPutKey();
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if (dataGetBank && dataGetBank.data) {
      setBank(dataGetBank.data);
    }
  }, [dataGetBank]);

  useEffect(() => {
    if (errorGetBank) {
      setErrorMessage(errorGetBank);
    }
  }, [errorGetBank]);

  useEffect(() => {
    if (
      bank &&
      bank.interface &&
      JSON.stringify(bank.interface) !== "{}" &&
      messages &&
      messages.length > 0
    ) {
      const result: { name: string; type: string }[] = Object.entries(bank.interface).map(
        ([key, value]) => ({
          name: key,
          // eslint-disable-next-line no-nested-ternary
          type: Array.isArray(value) ? "array" : value === null ? "null" : typeof value,
        }),
      );
      buildFirstData(bank.interface, messages);
      setTotalRows(messages.length);
      setInterfaceKeys(result);
    }
  }, [bank, messages]);

  useEffect(() => {
    if (isFirstRender.current) return;
    if (isSuccessDeleteKey || isSuccessPostKey || isSuccessPutKey) {
      refreshPage();
    }
  }, [isSuccessDeleteKey, isSuccessPostKey, isSuccessPutKey]);

  useEffect(() => {
    if (isFirstRender.current) return;
    getInitialData();
  }, [messageTypeSelected]);

  useEffect(() => {
    launchIsLoading();
    getInitialData();
    isFirstRender.current = false;
  }, []);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      <div
        className="generalStyles__pageHeader"
        style={{ display: "flex", justifyContent: "space-between" }}
      >
        <div>
          <div className="generalStyles__flex">
            <h5 className="generalStyles__info">{bank.name || "--"}</h5>
          </div>
        </div>
        <div className="generalStyles__flex ">
          <div className=" generalStyles__width100">
            <SelectMessageType setFilteredValue={onChangeMessageType} />
          </div>
          <div>
            <ButtonSquareRefresh
              onClick={refreshPage}
              isLoading={isLoading}
              disabledButton={isLoading}
            />
          </div>
          <div>
            <ButtonSquareDownloadCSV
              interfaceJson={bank.interface}
              fileName={String(bank.name ?? "")}
              data={data}
              disabledButton={isLoading}
              isLoading={isLoading}
            />
          </div>
          <div>
            <ButtonSquareUploadCSV
              setUploadCSVData={setUploadCSVData}
              isLoading={isLoading}
              disabledButton={isLoading}
            />
          </div>
          <div>
            <ButtonSquareCode
              onClick={handleOpenAssignInterface}
              isLoading={isLoading}
              disabledButton={isLoading}
            />
          </div>
          <div>
            <ButtonSquareSave
              onClick={onClickSave}
              disabledButton={isLoading}
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>
      <Table
        locale={{
          emptyText: (
            <>
              {isErrorGetBank ? (
                <TableNoDataBox
                  isError={isErrorGetBank}
                  errorMessage={
                    errorMessage && errorMessage.data && errorMessage.data.message
                      ? errorMessage.data.message
                      : "Error"
                  }
                  noItemsFound="No actions found"
                />
              ) : (
                <div>
                  {(bank.interface === null || JSON.stringify(bank.interface) === "{}") && (
                    <div className="generalStyles__flexCenter" style={{ height: "300px" }}>
                      <div>
                        <p>{t("general.interfaceNoAssigned")}</p>
                        <Button style={{ marginTop: "-5px" }} onClick={handleOpenAssignInterface}>
                          Assign Interface
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          ),
        }}
        rowClassName={(record, index) => getTableRowClass(index, theme)}
        dataSource={data}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ["10", "25", "50", "100"],
        }}
        rowKey={(record) => record.idMessage}
        size="small"
        className="mt-3"
        loading={
          isLoadingDeleteKey ||
          isFetchingGetBank ||
          isLoadingGetBank ||
          isLoadingPostKey ||
          isLoadingPutKey ||
          isLoading
        }
        footer={() => (
          <div className="generalStyles__flexEnd">
            <Tag>{totalRows} Rows</Tag>
          </div>
        )}
      >
        <Column
          title={t("general.state")}
          dataIndex="state"
          key="state"
          align="center"
          render={(text, record: any) => (
            <>
              {record.isValid ? (
                <FontAwesomeIcon className="generalStyles__success" icon={faCircleCheck} />
              ) : (
                <Tooltip title={t("general.keyDuplicated")}>
                  <FontAwesomeIcon className="generalStyles__error" icon={faXmarkCircle} />
                </Tooltip>
              )}
            </>
          )}
        />
        <Column
          title={t("general.message")}
          dataIndex="message"
          key="message"
          render={(text) => <>{text}</>}
        />
        <Column
          title={t("general.type")}
          dataIndex="type"
          key="type"
          render={(text) => <>{text}</>}
        />
        {interfaceKeys.map((item: { name: string; type: string }) => (
          <Column
            title={item.name}
            dataIndex={item.name}
            key={item.name}
            render={(text, record: any, index: number) => (
              <InputNumber
                bordered={false}
                className="configuration__inputSizeS"
                min={0}
                max={999}
                precision={0}
                value={record[item.name]}
                onChange={(e) => {
                  onChangeInputNumber(e, item.name, index);
                }}
              />
            )}
          />
        ))}
        {/** DELETE */}
        <Column
          title="Delete"
          dataIndex="delete"
          key="delete"
          render={(text, record: any) => (
            <Button
              onClick={() => {
                onClickDelete(record);
              }}
              shape="circle"
              className={!record.isValid ? "" : "buttonStyle__26"}
              disabled={!record.isValid || record.methodType === "POST"}
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          )}
        />
      </Table>
      <GlobalAlert2
        isError={isErrorDeleteKey}
        isSuccess={isSuccessDeleteKey}
        requestType="DELETE"
        error={errorDeleteKey}
        name="Key"
      />
      <GlobalAlert2
        isError={isErrorPostKey}
        isSuccess={isSuccessPostKey}
        requestType="POST"
        error={errorPostKey}
        name="Key"
      />
      <GlobalAlert2
        isError={isErrorPutKey}
        isSuccess={isSuccessPutKey}
        requestType="PUT"
        error={errorPutKey}
        name="Key"
      />
    </div>
  );
}

export default KeysBankTable;
